<template>
  <transition
    appear
    enter-active-class="transform ease-out duration-300"
    enter-class="opacity-0 -translate-x-full"
    enter-to-class="opacity-100 translate-x-0"
    leave-active-class=" transform ease-out duration-300"
    leave-class="opacity-100 translate-x-0"
    leave-to-class="opacity-0 -translate-x-full"
  >
    <div class="bg-ontick-graphite-500 fixed inset-0 z-10 h-full max-h-screen overflow-hidden" v-if="active">
      <!-- BACKGROUND -->
      <div class="absolute top-0 left-0 h-1/3 w-full">
        <img
          :src="`/img/${data.best_symbol.toLowerCase()}.jpg`"
          class="absolute top-0 left-0 h-full w-full object-cover"
        />
        <span class="gradient absolute inset-0 z-10"></span>
      </div>

      <!-- CONTENT -->
      <div class="pointer-events-auto absolute inset-0 z-10 overflow-y-auto px-4 pt-10 text-gray-50">
        <div class="min-h-content relative flex w-full flex-col gap-4 pb-32">
          <!-- <span
          class="bg-ontick-carbon-500 absolute top-2 left-1/2 h-1.5 w-14 -translate-x-1/2 rounded-full"
        ></span> -->
          <div class="flex items-center gap-2">
            <button class="flex h-10 w-10 items-center justify-center" @click="$emit('close')">
              <i v-html="$icons.chevron_shadow" />
            </button>
            <div class="flex items-center gap-2">
              <p class="text-shadow-lg text-2xl font-bold">
                {{ data.label }}
              </p>
            </div>
          </div>
          <div class="param-box !bg-ontick-graphite-500 !px-4 text-sm">
            <dl>
              <dt>Tendência:</dt>
              <dd>
                <BestSvgSparklineComponent
                  @click="openTrendModal"
                  class="h-10 !w-20"
                  :padding="0"
                  :symbol="data.best_symbol"
                />
              </dd>
            </dl>
            <dl>
              <dt>Target:</dt>
              <dd>
                <span v-if="selected_target == null" class="text-gray-400"> - </span>
                <span v-else> {{ selected_target }}{{ targetType(data.best_target_type) }} </span>
              </dd>
            </dl>
            <dl>
              <dt>Multiplicador:</dt>
              <dd>
                <span v-if="selected_multiplier == null" class="text-gray-400"> - </span>
                <span v-else> {{ selected_multiplier }}x </span>
              </dd>
            </dl>
            <dl>
              <dt>Cotação:</dt>
              <dd>
                <span v-if="!getSymbolPrice(data.best_symbol)" class="text-gray-400"> - </span>
                <span v-else>
                  {{ getSymbolPrice(data.best_symbol).toFixed(decimal_places) }}
                </span>
              </dd>
            </dl>
            <dl>
              <dt>Ganho:</dt>
              <dd>
                <span v-if="!pnl(data, selected_target, selected_multiplier)" class="text-gray-400"> - </span>
                <span v-else>
                  {{ pnl(data, selected_target, selected_multiplier) | currency }}
                </span>
              </dd>
            </dl>
            <dl>
              <dt>Perda:</dt>
              <dd>
                <span v-if="!pnl(data, selected_target, selected_multiplier)" class="text-gray-400"> - </span>
                <span v-else>
                  {{ pnl(data, selected_target, selected_multiplier) | currency }}
                </span>
              </dd>
            </dl>
            <dl>
              <dt>Custo de operação:</dt>
              <dd>
                {{ (data.best_cost * selected_multiplier) | currency }}
              </dd>
            </dl>
          </div>
          <div class="param-box">
            <p class="text-sm">Objetivo</p>
            <div class="flex w-full items-center gap-2">
              <button
                v-for="item in targets"
                :key="item"
                :disabled="blockedTargets.includes(item)"
                class="btn-shadow param-btn"
                :class="item == selected_target ? 'bg-ontick-blue-900' : 'bg-ontick-steel-500'"
                @click="selectTarget(item)"
              >
                <span class="text-shadow"> {{ item }}{{ targetType(data.best_target_type) }} </span>
              </button>
            </div>
          </div>
          <div class="param-box">
            <p class="text-sm">Parâmetros</p>
            <div class="flex w-full items-center gap-2">
              <button
                v-for="item in multipliers"
                :key="item"
                class="btn-shadow param-btn"
                :class="item == selected_multiplier ? 'bg-ontick-blue-900' : 'bg-ontick-steel-500'"
                @click="selectMultiplier(item)"
              >
                <span class="text-shadow"> {{ item }}x </span>
                <span class="text-shadow text-xs text-gray-300">
                  <template v-if="Number(pnl(data, selected_target, item))">
                    {{ pnl(data, selected_target, item) | currency }}
                  </template>
                  <template v-else> - </template>
                </span>
              </button>
            </div>
            <div class="flex items-center gap-2 uppercase">
              <div class="flex w-full items-center gap-2">
                <button
                  class="btn-shadow call-btn"
                  :class="selected_direction == 'BUY' ? 'bg-ontick-green-700' : 'bg-ontick-steel-500'"
                  @click="selected_direction = 'BUY'"
                >
                  <i v-html="$icons.best_call_long"></i>
                  <span class="text-shadow"> Sobe </span>
                </button>
              </div>
              <div class="flex w-full items-center gap-2">
                <button
                  class="btn-shadow call-btn"
                  :class="selected_direction == 'SELL' ? 'bg-ontick-orange-600' : 'bg-ontick-steel-500'"
                  @click="selected_direction = 'SELL'"
                >
                  <i v-html="$icons.best_call_short"></i>
                  <span class="text-shadow"> Desce </span>
                </button>
              </div>
            </div>
          </div>
          <p class="text-ontick-yellow-700 text-xs" v-if="selected_multiplier">
            Atenção: o saldo em conta necessário para este Best é de
            {{ (data.best_margin * selected_multiplier) | currency }}.
          </p>
          <button
            class="btn-shadow bg-ontick-blue-600 text-shadow-lg disabled:bg-ontick-steel-500 flex w-full items-center justify-center rounded-2xl py-4 text-xl font-bold uppercase disabled:pointer-events-none disabled:text-gray-500 disabled:shadow-none"
            :disabled="button_is_disabled || !(selected_target && selected_multiplier && selected_direction)"
            @click="
              start({
                best_data: data,
                type: selected_direction,
                selected_target,
                selected_multiplier,
              })
            "
          >
            <span class="uppercase"> Best! </span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from "vuex";
import event_bus from "../../../shared/utils/event_bus";
import symbol_images from "../symbol_images";
import startBest from "../start-best";
import predictedPnl from "../predicted_pnl";
import { iconsSet as icons } from "../../../assets/icons/icons";
import BestSvgSparklineComponent from "./best-svg-sparkline.component.vue";

export default {
  mixins: [startBest, predictedPnl, symbol_images],

  components: {
    BestSvgSparklineComponent,
  },

  props: {
    active: {
      type: Boolean,
    },
    data: {
      type: Object,
    },
  },

  data() {
    return {
      selected_target: null,
      selected_multiplier: null,
      selected_direction: null,
    };
  },

  computed: {
    ...mapGetters({
      getUserBests: "bests/getUserBests",
      getSymbolData: "bests/getSymbolData",
      getSymbolPrice: "marketdata/getSymbolPrice",
    }),
    decimal_places() {
      return this.getSymbolData.find((item) => item.symbol == this.data.best_symbol).decimal_places;
    },
    targets() {
      return this.data.best_targets.split(",").map((item) => Number(item));
    },
    multipliers() {
      return this.data.best_multipliers.split(",").map((item) => Number(item));
    },
    blockedTargets() {
      const same_symbol_running_bests = this.getUserBests.filter((item) => item.bo_symbol == this.data.best_symbol);
      if (same_symbol_running_bests.length > 0) {
        return same_symbol_running_bests.map((item) => item.bo_target);
      }
      return [];
    },
    trendInfo() {
      const trend = this.getSymbolData.find((item) => item.symbol == this.data.best_symbol).symbol_trend;
      switch (trend) {
        case "UP":
          return {
            trend,
            icon: icons.trend_up,
            tooltip: "Tendência de alta",
          };
        case "DOWN":
          return {
            trend,
            icon: icons.trend_down,
            tooltip: "Tendência de baixa",
          };
        default:
          return {
            trend,
            icon: icons.trend_sideways,
            tooltip: "Tendência lateral",
          };
      }
    },
  },

  methods: {
    openTrendModal() {
      event_bus.$emit("OPEN_TREND_MODAL");
    },
    selectTarget(e) {
      this.selected_target = e;
    },
    selectMultiplier(e) {
      this.selected_multiplier = e;
    },
    bestCall(e) {
      event_bus.$emit("BEST_CALL", e);
    },
    targetType(best_target_type) {
      switch (best_target_type) {
        case "POINTS":
          return "p";
        case "PERCENT":
          return "%";
        default:
          return "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gradient {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #202a37 69.82%);
}

.btn-shadow {
  box-shadow: 0px 4px 4px 1px rgba(0, 0, 0, 0.35);
}

.param-box {
  @apply bg-ontick-carbon-500 flex flex-col gap-2 rounded-2xl p-3;
}

.param-btn {
  @apply flex w-full flex-col items-center justify-center rounded-xl py-2 active:brightness-75 disabled:text-gray-500 disabled:opacity-50;
}

.text-shadow {
  text-shadow: 0px 2px 1px rgba(0, 0, 0, 0.5);

  &-lg {
    text-shadow: 0px 3px 2px rgba(0, 0, 0, 0.5);
  }
}

.call-btn {
  @apply flex w-full items-center justify-center gap-2 rounded-xl py-3 filter active:brightness-75;
}

dl {
  @apply flex items-center justify-between;

  dd {
    @apply font-semibold;
  }
}
</style>
